import { useState } from 'react';
import type { User } from '../../lib/api';

interface Props {
  user: User;
  signedIn: boolean;
  setSignedIn: (v: boolean) => void;
  onGoToPin?: () => void;
}

// `setSignedIn` is intentionally unused: the right-side popup is rendered as a
// non-interactive image. Real sign-in happens in the user's actual Chrome
// extension; SignInStep's auto-detect (extension_last_seen polling) flips
// `detected` directly when the real extension pings.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export default function ExtensionPopupVisual({ user, signedIn, setSignedIn: _, onGoToPin }: Props) {
  const [showRealToolbarModal, setShowRealToolbarModal] = useState(false);

  return (
    <div
      className="flex flex-wrap items-start relative"
      style={{ maxWidth: 780, gap: 28 }}
    >
      {/* Browser frame with toolbar showing pinned go/ icon */}
      <div
        className="bg-surface overflow-hidden"
        style={{
          flex: '1 1 340px',
          maxWidth: 400,
          borderRadius: 10,
          border: '1px solid var(--color-hairline)',
          boxShadow: 'var(--shadow-md)',
        }}
      >
        <div
          className="flex items-center"
          style={{
            gap: 10,
            padding: '9px 12px',
            background: 'oklch(0.94 0.01 85)',
            borderBottom: '1px solid var(--color-hairline)',
          }}
        >
          <div className="flex" style={{ gap: 5 }}>
            <div className="w-[9px] h-[9px] rounded-full" style={{ background: 'oklch(0.72 0.17 25)' }} />
            <div className="w-[9px] h-[9px] rounded-full" style={{ background: 'oklch(0.85 0.16 85)' }} />
            <div className="w-[9px] h-[9px] rounded-full" style={{ background: 'oklch(0.76 0.16 145)' }} />
          </div>
        </div>
        <div className="flex" style={{ padding: '4px 8px 0', background: 'oklch(0.94 0.01 85)' }}>
          <div
            className="flex items-center bg-surface font-medium text-ink"
            style={{
              gap: 6,
              padding: '6px 12px 7px',
              borderTopLeftRadius: 7,
              borderTopRightRadius: 7,
              border: '1px solid var(--color-hairline)',
              borderBottom: 'none',
              fontSize: 11,
            }}
          >
            <div
              className="rounded font-mono font-extrabold flex items-center justify-center"
              style={{
                width: 12,
                height: 12,
                borderRadius: 2,
                background: 'var(--accent-soft)',
                color: 'var(--accent-deep)',
                fontSize: 6,
              }}
            >
              go/
            </div>
            <span>New Tab</span>
          </div>
        </div>
        <div className="flex items-center" style={{ gap: 6, padding: '9px 10px' }}>
          <div
            className="flex-1 font-mono text-ink-muted"
            style={{
              background: 'var(--color-bg-alt)',
              border: '1px solid var(--color-hairline)',
              borderRadius: 999,
              padding: '5px 11px',
              fontSize: 10,
            }}
          >
            chrome://newtab
          </div>
          <button
            onClick={() => setShowRealToolbarModal(true)}
            className="font-mono font-extrabold flex items-center justify-center cursor-pointer relative"
            style={{
              width: 30,
              height: 30,
              background: 'var(--accent-soft)',
              color: 'var(--accent-deep)',
              border: '2px solid var(--accent-solid)',
              borderRadius: 7,
              fontSize: 11,
              boxShadow: '0 0 0 4px color-mix(in oklch, var(--accent-solid) 13%, transparent)',
              animation: 'c2pulseRing 1.8s ease-in-out infinite',
            }}
          >
            go/
            <div
              className="absolute font-semibold whitespace-nowrap"
              style={{
                top: -30,
                right: -6,
                background: 'var(--color-ink)',
                color: '#fff',
                padding: '4px 10px',
                borderRadius: 999,
                fontSize: 10.5,
                animation: 'c2bounce 1.8s ease-in-out infinite',
              }}
            >
              Click the go/ icon
              <span
                className="absolute"
                style={{
                  bottom: -4,
                  right: 10,
                  width: 0,
                  height: 0,
                  borderLeft: '4px solid transparent',
                  borderRight: '4px solid transparent',
                  borderTop: '4px solid var(--color-ink)',
                }}
              />
            </div>
          </button>
          <div
            className="rounded-full flex items-center justify-center font-bold"
            style={{
              width: 30,
              height: 30,
              background: 'var(--color-lemon)',
              color: 'var(--color-lemon-deep)',
              fontSize: 11,
            }}
          >
            R
          </div>
        </div>
        <div
          className="bg-bg"
          style={{
            borderTop: '1px solid var(--color-hairline)',
            minHeight: 170,
          }}
        />
      </div>

      {/* Extension popup — purely decorative; real sign-in happens in the user's
          actual Chrome extension. pointerEvents:none kills clicks/focus on every
          descendant input and button so this is effectively a static image. */}
      <div
        className="bg-surface overflow-hidden relative"
        style={{
          width: 320,
          borderRadius: 20,
          border: '1px solid var(--color-hairline-strong)',
          boxShadow: 'var(--shadow-lg)',
          animation: 'c2fadeSlide 260ms ease-out',
          pointerEvents: 'none',
          userSelect: 'none',
        }}
      >
        {/* Pointer triangle */}
        <div
          className="absolute"
          style={{
            top: -7,
            left: 30,
            width: 12,
            height: 12,
            background: 'var(--accent-tint)',
            borderTop: '1px solid var(--color-hairline-strong)',
            borderLeft: '1px solid var(--color-hairline-strong)',
            transform: 'rotate(45deg)',
          }}
        />
        <div
          className="flex items-center"
          style={{
            gap: 10,
            padding: '14px 16px',
            borderBottom: '1px solid var(--color-hairline)',
            background: 'var(--accent-tint)',
          }}
        >
          <div
            className="flex items-center justify-center font-mono font-extrabold"
            style={{
              width: 28,
              height: 28,
              borderRadius: 7,
              background: 'var(--accent-soft)',
              color: 'var(--accent-deep)',
              fontSize: 11,
            }}
          >
            go/
          </div>
          <span className="font-bold text-ink" style={{ fontSize: 13 }}>
            Tidy Go Links
          </span>
        </div>
        {signedIn ? (
          <div className="text-center" style={{ padding: '22px 16px 18px' }}>
            <div
              className="rounded-full mx-auto flex items-center justify-center"
              style={{
                width: 48,
                height: 48,
                marginBottom: 12,
                background: 'oklch(0.92 0.08 150)',
                color: 'oklch(0.34 0.1 150)',
                animation: 'c2pulseIn 360ms ease-out',
              }}
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </div>
            <div className="font-bold text-ink mb-1" style={{ fontSize: 15 }}>You're signed in!</div>
            <div className="text-ink-soft" style={{ fontSize: 12 }}>{user.email}</div>
          </div>
        ) : (
          <div style={{ padding: '22px 16px 18px' }}>
            <div
              className="font-display font-bold text-ink m-0"
              style={{ fontSize: 19, letterSpacing: '-0.01em', marginBottom: 4 }}
            >
              Welcome back
            </div>
            <div className="text-ink-soft mb-4" style={{ fontSize: 12 }}>
              Sign in to finish setting up.
            </div>
            <input
              value={user.email}
              readOnly
              className="mb-2.5 w-full"
              style={{
                padding: '8px 11px',
                fontSize: 12.5,
                border: '1px solid var(--color-hairline-strong)',
                borderRadius: 14,
                fontFamily: 'inherit',
                color: 'var(--color-ink)',
                background: 'var(--color-surface)',
                outline: 'none',
              }}
            />
            <button
              type="button"
              tabIndex={-1}
              className="flex items-center justify-center gap-2 w-full font-semibold"
              style={{
                padding: '9px 12px',
                background: 'var(--accent-solid)',
                color: '#fff',
                border: 'none',
                borderRadius: 8,
                fontSize: 13,
              }}
            >
              Sign in with magic link
            </button>
            <div className="flex items-center" style={{ gap: 8, margin: '14px 0 10px' }}>
              <div className="flex-1" style={{ height: 1, background: 'var(--color-hairline)' }} />
              <span className="font-medium text-ink-muted" style={{ fontSize: 10.5 }}>OR</span>
              <div className="flex-1" style={{ height: 1, background: 'var(--color-hairline)' }} />
            </div>
            <button
              type="button"
              tabIndex={-1}
              className="flex items-center justify-center w-full font-medium text-ink"
              style={{
                gap: 8,
                padding: '8px 12px',
                background: 'var(--color-surface)',
                border: '1px solid var(--color-hairline-strong)',
                borderRadius: 8,
                fontSize: 12.5,
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
              </svg>
              Continue with Google
            </button>
          </div>
        )}
      </div>

      {/* Modal: shown when user clicks the demo go/ icon. Redirects them to
          their actual Chrome toolbar. */}
      {showRealToolbarModal && (
        <div
          className="fixed inset-0 flex items-center justify-center"
          style={{
            background: 'oklch(0 0 0 / 0.45)',
            zIndex: 100,
            cursor: 'pointer',
            animation: 'c2modalBackdrop 180ms ease-out',
          }}
          onClick={() => setShowRealToolbarModal(false)}
        >
          <div
            className="bg-surface text-center"
            style={{
              padding: '32px 32px 26px',
              borderRadius: 18,
              boxShadow: '0 24px 60px -12px rgba(0,0,0,0.4)',
              maxWidth: 380,
              margin: '0 24px',
              cursor: 'default',
              animation: 'c2modalIn 220ms cubic-bezier(0.34, 1.56, 0.64, 1)',
            }}
            onClick={e => e.stopPropagation()}
          >
            <div
              className="rounded-full mx-auto flex items-center justify-center"
              style={{
                width: 56,
                height: 56,
                marginBottom: 14,
                background: 'var(--accent-soft)',
                color: 'var(--accent-deep)',
              }}
            >
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="12" y1="19" x2="12" y2="5" />
                <polyline points="5 12 12 5 19 12" />
              </svg>
            </div>
            <div
              className="font-display font-bold text-ink"
              style={{ fontSize: 22, letterSpacing: '-0.01em', marginBottom: 8 }}
            >
              Up in your real toolbar.
            </div>
            <div className="text-ink-soft" style={{ fontSize: 14, lineHeight: 1.5, marginBottom: 22 }}>
              This is just a demo. Click the actual <span className="font-mono font-bold text-ink" style={{ background: 'var(--accent-soft)', color: 'var(--accent-deep)', padding: '1px 6px', borderRadius: 4 }}>go/</span> icon at the top of your Chrome window to sign in.
              <div style={{ marginTop: 10 }}>
                If you don't see the <span className="font-mono font-bold text-ink" style={{ background: 'var(--accent-soft)', color: 'var(--accent-deep)', padding: '1px 6px', borderRadius: 4 }}>go/</span> icon among your other extensions, you still need to{' '}
                <button
                  type="button"
                  onClick={() => {
                    setShowRealToolbarModal(false);
                    onGoToPin?.();
                  }}
                  className="font-semibold cursor-pointer"
                  style={{
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    color: 'var(--accent-deep)',
                    textDecoration: 'underline',
                    fontSize: 'inherit',
                    fontFamily: 'inherit',
                  }}
                >
                  pin it
                </button>.
              </div>
            </div>
            <button
              type="button"
              onClick={() => setShowRealToolbarModal(false)}
              className="font-semibold cursor-pointer"
              style={{
                padding: '10px 22px',
                background: 'var(--color-ink)',
                color: '#fff',
                border: 'none',
                borderRadius: 999,
                fontSize: 13,
              }}
            >
              Got it
            </button>
          </div>
        </div>
      )}

      <style>{`
        @keyframes c2pulseRing {
          0%, 100% { box-shadow: 0 0 0 4px color-mix(in oklch, var(--accent-solid) 13%, transparent); }
          50% { box-shadow: 0 0 0 8px color-mix(in oklch, var(--accent-solid) 7%, transparent); }
        }
        @keyframes c2bounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-3px); }
        }
        @keyframes c2fadeSlide {
          from { opacity: 0; transform: translateY(6px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes c2pulseIn {
          0% { opacity: 0; transform: scale(0.6); }
          60% { opacity: 1; transform: scale(1.08); }
          100% { opacity: 1; transform: scale(1); }
        }
        @keyframes c2modalBackdrop {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes c2modalIn {
          0% { opacity: 0; transform: scale(0.85); }
          100% { opacity: 1; transform: scale(1); }
        }
      `}</style>
    </div>
  );
}
